import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Self,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { FocusMonitor } from '@angular/cdk/a11y';

@Component({
  standalone: false,
  selector: 'nimbus-filter-number-range-input',
  templateUrl: './nimbus-filter-number-range-input.component.html',
  styleUrls: ['./nimbus-filter-number-range-input.component.scss'],
  providers: [{ provide: MatFormFieldControl, useExisting: NimbusFilterNumberRangeInputComponent }],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NimbusFilterNumberRangeInputComponent implements MatFormFieldControl<number>, ControlValueAccessor, OnInit, OnDestroy {
  public static nextId: number = 0;

  @ViewChild('numberInput', { static: true })
  public numberInput: ElementRef<HTMLInputElement>;

  @Input() public minValue?: number | null;
  @Input() public maxValue?: number | null;
  @Input() public defaultValue?: number | null;
  @Input() public step: number = 1;
  @Input() public unit?: string;

  @HostBinding() public id: string = `nimbus-range-input-${NimbusFilterNumberRangeInputComponent.nextId++}`;

  private internalPlaceholder: string;
  private internalRequired: boolean = false;
  private internalDisabled: boolean = false;

  public readonly controlType: string = 'range-input';
  public readonly autofilled: boolean = false;
  public readonly stateChanges: Subject<void> = new Subject<void>();
  public readonly userAriaDescribedBy: string;
  public focused: boolean = false;
  public touched: boolean = false;
  public control: FormControl<number | null> = new FormControl<number | null>(null);

  @Input()
  public get value(): number | null {
    const raw = this.control.value;
    return raw == null ? (this.defaultValue ?? null) : Number(raw);
  }

  @Input()
  public get placeholder(): string {
    return this.internalPlaceholder;
  }

  @Input()
  public get required(): boolean {
    return this.internalRequired;
  }

  @Input()
  public get disabled(): boolean {
    return this.internalDisabled;
  }

  public constructor(
    @Optional() @Self() public ngControl: NgControl,
    private readonly focusMonitor: FocusMonitor,
    private readonly elementRef: ElementRef<HTMLElement>,
  ) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  public ngOnInit(): void {
    if (this.control.value == null && this.defaultValue != null) {
      this.control.setValue(this.defaultValue, { emitEvent: false });
    }
  }

  public onChange: (value: number | null) => void = () => {};
  public onTouched: () => void = () => {};

  public set value(val: number | null) {
    this.control.setValue(val == null ? (this.defaultValue ?? null) : Number(val), { emitEvent: false });
    this.stateChanges.next();
  }

  public set placeholder(val: string) {
    this.internalPlaceholder = val;
    this.stateChanges.next();
  }

  public set required(req: boolean) {
    this.internalRequired = coerceBooleanProperty(req);
    this.stateChanges.next();
  }

  public set disabled(value: boolean) {
    this.internalDisabled = coerceBooleanProperty(value);
    this.stateChanges.next();
  }

  public get empty(): boolean {
    return this.control.value == null;
  }

  public get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  public get errorState(): boolean {
    return this.control.invalid && this.touched;
  }

  public ngOnDestroy(): void {
    this.stateChanges.complete();
    this.focusMonitor.stopMonitoring(this.elementRef);
  }

  public onContainerClick(event: MouseEvent): void {
    if ((event.target as Element).tagName.toLowerCase() !== 'input') {
      this.numberInput?.nativeElement?.focus();
    }
  }

  public setDescribedByIds(ids: string[]): void {
    const controlElement = this.elementRef.nativeElement.querySelector('.nimbus-range-input-container');
    controlElement?.setAttribute('aria-describedby', ids.join(' '));
  }

  public writeValue(val: number | null): void {
    this.value = val;
  }

  public registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public onFocusIn(): void {
    if (!this.focused) {
      this.focused = true;
      this.stateChanges.next();
      this.focusMonitor.focusVia(this.numberInput, 'program');
      this.numberInput.nativeElement.select();
    }
  }

  public onFocusOut(event: FocusEvent): void {
    if (!this.elementRef.nativeElement.contains(event.relatedTarget as Element)) {
      this.touched = true;
      this.focused = false;
      this.onTouched();
      this.stateChanges.next();
    }
  }

  public handleInput(): void {
    const raw = this.numberInput.nativeElement.value;
    const next = raw === '' || raw == null ? null : Number(raw);
    this.control.setValue(next, { emitEvent: false });
    this.onChange(next);
    this.stateChanges.next();
  }

  public onBlur(): void {
    if (this.defaultValue == null) {
      return;
    }
    const raw = this.numberInput.nativeElement.value;
    if (raw === '' || raw == null) {
      this.control.setValue(this.defaultValue, { emitEvent: false });
      this.onChange(this.defaultValue);
      this.stateChanges.next();
    }
  }
}
